import * as FileSystem from "expo-file-system/legacy";

import { isSupabaseConfigured, supabase } from "./supabase";

const MEDIA_BUCKET = "media";
const BASE64_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const guessContentType = (uri: string) => {
  const normalizedUri = uri.toLowerCase();

  if (normalizedUri.endsWith(".png")) return "image/png";
  if (normalizedUri.endsWith(".webp")) return "image/webp";
  if (normalizedUri.endsWith(".gif")) return "image/gif";
  if (normalizedUri.endsWith(".mov")) return "video/quicktime";
  if (normalizedUri.endsWith(".mp4")) return "video/mp4";

  return "image/jpeg";
};

const guessExtension = (uri: string) => {
  const match = uri.split("?")[0]?.match(/\.([a-zA-Z0-9]+)$/);
  if (match?.[1]) return match[1].toLowerCase();

  return guessContentType(uri).startsWith("video/") ? "mp4" : "jpg";
};

export const isLocalMediaUri = (uri?: string) => {
  if (!uri) return false;
  return (
    uri.startsWith("file://") ||
    uri.startsWith("ph://") ||
    uri.startsWith("assets-library://") ||
    uri.startsWith("content://") ||
    uri.startsWith("blob:")
  );
};

const base64ToArrayBuffer = (base64: string) => {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;

  base64
    .replace(/[\n\r\s=]/g, "")
    .split("")
    .forEach((character) => {
      const value = BASE64_CHARS.indexOf(character);
      if (value === -1) return;

      buffer = (buffer << 6) | value;
      bits += 6;

      if (bits >= 8) {
        bits -= 8;
        bytes.push((buffer >> bits) & 0xff);
      }
    });

  return Uint8Array.from(bytes).buffer;
};

const readLocalUriAsArrayBuffer = async (uri: string) => {
  try {
    const base64 = await FileSystem.readAsStringAsync(uri, {
      encoding: FileSystem.EncodingType.Base64,
    });

    return base64ToArrayBuffer(base64);
  } catch (fileSystemError) {
    console.warn("Failed to read media with FileSystem", fileSystemError);

    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error(`Failed to read media file: ${response.status}`);
    }

    return response.arrayBuffer();
  }
};

export const uploadMediaAsync = async (
  uri: string,
  pathPrefix: string,
): Promise<string> => {
  if (!isSupabaseConfigured || !isLocalMediaUri(uri)) {
    return uri;
  }

  try {
    const contentType = guessContentType(uri);
    const extension = guessExtension(uri);
    const storagePath = `${pathPrefix}/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}.${extension}`;

    const fileBody = await readLocalUriAsArrayBuffer(uri);

    const { error } = await supabase.storage
      .from(MEDIA_BUCKET)
      .upload(storagePath, fileBody, {
        contentType,
        cacheControl: "31536000",
        upsert: true,
      });

    if (error) {
      throw new Error(error.message);
    }

    const { data } = supabase.storage
      .from(MEDIA_BUCKET)
      .getPublicUrl(storagePath);

    return data.publicUrl;
  } catch (error) {
    console.warn("Failed to upload media", error);
    throw error;
  }
};

export const uploadMediaListAsync = async (
  uris: string[] | undefined,
  pathPrefix: string,
) => {
  if (!uris || uris.length === 0) return undefined;

  const uploadedUris = await Promise.all(
    uris.map((uri) => uploadMediaAsync(uri, pathPrefix)),
  );

  return uploadedUris;
};
